import React, { useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';

import { useLanguage } from '../../context/LanguageProvider/LanguageProvider';
import {
  SHOWCASE_PROJECT_DISPLAY_ORDER,
  SHOWCASE_PROJECT_IDS,
  useAdminData,
} from '../../context/AdminDataProvider/AdminDataProvider';

const STATUSES = ['active', 'in_progress', 'completed'];

const STATUS_BADGE_CLASS = {
  active: 'badge bg-success text-white',
  in_progress: 'badge bg-primary text-white',
  completed: 'badge bg-dark text-white',
};

const MAX_IMAGE_BYTES = 512 * 1024;

const emptyProject = {
  name: '',
  domainId: '',
  status: 'active',
  description: '',
  logoUrl: '',
  photoUrl: '',
  websiteUrl: '',
};

function readImageFile(file, onDataUrl, onError) {
  if (!file || !file.type.startsWith('image/')) {
    onError?.('invalid');
    return;
  }
  if (file.size > MAX_IMAGE_BYTES) {
    onError?.('large');
    return;
  }
  const reader = new FileReader();
  reader.onload = () => onDataUrl(String(reader.result || ''));
  reader.onerror = () => onError?.('read');
  reader.readAsDataURL(file);
}

export default function AdminProjectsPage() {
  const { t } = useLanguage();
  const { projects, domains, addProject, updateProject, deleteProject } = useAdminData();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const displayProjects = useMemo(() => {
    const byId = new Map(projects.map((p) => [p.id, p]));
    const orderedShowcase = SHOWCASE_PROJECT_DISPLAY_ORDER.map((id) => byId.get(id)).filter(Boolean);
    const rest = projects.filter((p) => !SHOWCASE_PROJECT_IDS.has(p.id));
    return [...orderedShowcase, ...rest];
  }, [projects]);

  const domainById = useMemo(() => {
    const m = {};
    domains.forEach((d) => {
      m[d.id] = d;
    });
    return m;
  }, [domains]);

  const openAdd = () => {
    setEditingId(null);
    setForm({
      ...emptyProject,
      domainId: domains[0]?.id || '',
    });
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name,
      domainId: p.domainId || domains[0]?.id || '',
      status: p.status || 'active',
      description: p.description || '',
      logoUrl: p.logoUrl || '',
      photoUrl: p.photoUrl || '',
      websiteUrl: p.websiteUrl || '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.domainId) return;
    if (editingId) {
      await updateProject(editingId, form);
    } else {
      await addProject(form);
    }
    closeModal();
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('admin.crud.confirmDelete'))) {
      await deleteProject(id);
    }
  };

  return (
    <div className="jassp-admin-crud">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 className="h3 fw-bold text-dark mb-1">{t('admin.crud.projectsTitle')}</h1>
          <p className="text-muted small mb-0">{t('admin.crud.projectsShowcaseHint')}</p>
        </div>
        <Button
          variant="primary"
          className="rounded-pill px-4"
          onClick={openAdd}
          disabled={domains.length === 0}
        >
          {t('admin.crud.addProject')}
        </Button>
      </div>

      {domains.length === 0 && (
        <Alert variant="warning" className="mb-4">
          {t('admin.crud.noDomainsHint')}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                <th className="ps-4">{t('admin.crud.colName')}</th>
                <th>{t('admin.crud.colVisuals')}</th>
                <th>{t('admin.crud.colDomain')}</th>
                <th>{t('admin.crud.colStatus')}</th>
                <th>{t('admin.crud.colDescription')}</th>
                <th className="text-end pe-4">{t('admin.crud.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {displayProjects.map((p) => (
                <tr key={p.id}>
                  <td className="ps-4 fw-medium">
                    {p.name}
                    {SHOWCASE_PROJECT_IDS.has(p.id) ? (
                      <span className="badge bg-secondary bg-opacity-25 text-dark border ms-2 align-middle small">
                        {t('admin.crud.staticProjectBadge')}
                      </span>
                    ) : null}
                  </td>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      {p.logoUrl ? (
                        <img
                          src={p.logoUrl}
                          alt=""
                          className="rounded border bg-light"
                          style={{ width: 40, height: 40, objectFit: 'contain' }}
                        />
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                      {p.photoUrl ? (
                        <img
                          src={p.photoUrl}
                          alt=""
                          className="rounded border bg-light"
                          style={{ width: 56, height: 36, objectFit: 'cover' }}
                        />
                      ) : null}
                    </div>
                  </td>
                  <td>{domainById[p.domainId]?.name || p.domainId || '—'}</td>
                  <td>
                    <span className={STATUS_BADGE_CLASS[p.status] || STATUS_BADGE_CLASS.active}>
                      {t(`admin.crud.projectStatus.${p.status || 'active'}`)}
                    </span>
                  </td>
                  <td className="text-muted small">{p.description}</td>
                  <td className="text-end pe-4">
                    <Button variant="link" size="sm" className="text-primary p-1" onClick={() => openEdit(p)}>
                      {t('admin.crud.edit')}
                    </Button>
                    <Button variant="link" size="sm" className="text-danger p-1" onClick={() => handleDelete(p.id)}>
                      {t('admin.crud.delete')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={modalOpen} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? t('admin.crud.editProject') : t('admin.crud.addProject')}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colName')}</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colDomain')}</Form.Label>
              <Form.Select
                value={form.domainId}
                onChange={(e) => setForm((f) => ({ ...f, domainId: e.target.value }))}
                required
              >
                {domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colStatus')}</Form.Label>
              <Form.Select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {t(`admin.crud.projectStatus.${s}`)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.colDescription')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.projectWebsite')}</Form.Label>
              <Form.Control
                type="url"
                placeholder="https://"
                value={form.websiteUrl}
                onChange={(e) => setForm((f) => ({ ...f, websiteUrl: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('admin.crud.projectLogo')}</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  readImageFile(
                    file,
                    (url) => setForm((f) => ({ ...f, logoUrl: url })),
                    (err) => {
                      e.target.value = '';
                      if (err === 'large') window.alert(t('admin.crud.imageTooLarge'));
                      else if (err === 'invalid') window.alert(t('admin.crud.imageInvalid'));
                    },
                  );
                }}
              />
              {form.logoUrl ? (
                <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                  <img
                    src={form.logoUrl}
                    alt=""
                    className="rounded border bg-light"
                    style={{ maxHeight: 72, maxWidth: 120, objectFit: 'contain' }}
                  />
                  <Button type="button" variant="outline-danger" size="sm" onClick={() => setForm((f) => ({ ...f, logoUrl: '' }))}>
                    {t('admin.crud.removeImage')}
                  </Button>
                </div>
              ) : null}
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('admin.crud.projectPhoto')}</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  readImageFile(
                    file,
                    (url) => setForm((f) => ({ ...f, photoUrl: url })),
                    (err) => {
                      e.target.value = '';
                      if (err === 'large') window.alert(t('admin.crud.imageTooLarge'));
                      else if (err === 'invalid') window.alert(t('admin.crud.imageInvalid'));
                    },
                  );
                }}
              />
              {form.photoUrl ? (
                <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                  <img
                    src={form.photoUrl}
                    alt=""
                    className="rounded border bg-light"
                    style={{ maxHeight: 100, maxWidth: '100%', objectFit: 'cover' }}
                  />
                  <Button type="button" variant="outline-danger" size="sm" onClick={() => setForm((f) => ({ ...f, photoUrl: '' }))}>
                    {t('admin.crud.removeImage')}
                  </Button>
                </div>
              ) : null}
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" type="button" onClick={closeModal}>
              {t('admin.crud.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={domains.length === 0}>
              {t('admin.crud.save')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
}
